import {Controller} from '@hotwired/stimulus';

/**
 * Compteur de non-lus d'un lien (nouveautés Modilog de la topbar). Le compte est demandé à `countUrl`
 * (JSON `{count, token}`) une fois la page affichée, jamais pendant son rendu, puis gardé `cacheTtl` secondes
 * en `sessionStorage` sous `storageKey` pour ne pas le redemander à chaque page. Un clic ou un clic molette sur
 * le lien retire le badge et le signale à `seenUrl` (POST, `_token` = le `token` reçu avec le compte, même s'il
 * arrive après le clic). Au-delà de `max`, le badge affiche `max+`. Sans réponse exploitable, pas de badge : rien
 * d'autre n'en dépend.
 *
 *     <a href="…" target="_blank" {{ stimulus_controller('unread-badge', {countUrl: path('modilog.unread'),
 *         seenUrl: path('modilog.seen'), storageKey: 'modilog-unread:' ~ userId}) }}
 *         {{ stimulus_action('unread-badge', 'seen', 'click') | stimulus_action('unread-badge', 'seen', 'auxclick') }}>
 *       {{ lucide('sparkles', {size: 16}) }}
 *       <span class="badge" hidden {{ stimulus_target('unread-badge', 'badge') }}></span>
 *     </a>
 */
export default class extends Controller {
  static targets = ['badge'];
  static values = {
    countUrl: String,
    seenUrl: String,
    storageKey: String,
    cacheTtl: {type: Number, default: 300},
    max: {type: Number, default: 9},
  };

  connect() {
    const cached = this.readCache();

    if (cached === null) {
      this.load();
    } else {
      this.token = cached.token;
      this.show(cached.count);
    }
  }

  async load() {
    try {
      const response = await fetch(this.countUrlValue, {
        headers: {'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
      });

      if (!response.ok) {
        return;
      }

      const {count, token} = await response.json();

      if (Number.isInteger(count)) {
        this.token = typeof token === 'string' ? token : null;

        // Lien cliqué avant la réponse : le compte est déjà lu, il reste à le signaler.
        if (this.opened) {
          this.writeCache(0);
          this.sendSeen();
          return;
        }

        this.writeCache(count);
        this.show(count);
      }
    } catch {
      // pas de badge
    }
  }

  seen(event) {
    // auxclick : seul le clic molette ouvre le lien, le clic droit n'ouvre qu'un menu.
    if (event?.type === 'auxclick' && event.button !== 1) {
      return;
    }

    this.opened = true;
    this.writeCache(0);
    this.show(0);
    this.sendSeen();
  }

  sendSeen() {
    // Jeton pas encore reçu : load() signalera à son arrivée.
    if (!this.token) {
      return;
    }

    const body = new FormData();
    body.append('_token', this.token);

    // keepalive : l'envoi survit si l'onglet du manager est quitté juste après le clic.
    fetch(this.seenUrlValue, {
      method: 'POST',
      body,
      keepalive: true,
      headers: {'X-Requested-With': 'XMLHttpRequest'},
    }).catch(() => {});
  }

  show(count) {
    this.badgeTarget.textContent = count > this.maxValue ? `${this.maxValue}+` : String(count);
    this.badgeTarget.hidden = count === 0;
  }

  readCache() {
    try {
      const entry = JSON.parse(sessionStorage.getItem(this.storageKeyValue) ?? 'null');

      const fresh = Date.now() - entry?.at < this.cacheTtlValue * 1000;

      if (Number.isInteger(entry?.count) && typeof entry.token === 'string' && fresh) {
        return entry;
      }
    } catch {
      // stockage indisponible ou entrée illisible : on redemande
    }

    return null;
  }

  writeCache(count) {
    try {
      sessionStorage.setItem(this.storageKeyValue, JSON.stringify({count, token: this.token, at: Date.now()}));
    } catch {
      // le compte sera redemandé à la page suivante
    }
  }
}
